import * as z from 'zod'

import { CreateSiteVars, UpdateSiteVars } from '@/common/api/params/site'
import { NameLabelMapping } from '@/common/app/message'
import { StringLength } from '@/common/form/schema'
import { Site } from '@/common/model/site'

export const siteSchema = z.object({
  id: z.number().readonly().optional(),
  name: z.string().min(1).max(StringLength.NORMAL_MAX),
  domain: z.url().min(1).max(StringLength.NORMAL_MAX),
  projectNumber: z
    .string()
    .min(1)
    .max(StringLength.NORMAL_MAX)
    .regex(/^[0-9a-zA-Z-_]+$/, '半角英数字とハイフンとアンダーバーのみ使用できます')
    .refine((v) => v.toLowerCase() !== 'w2', { message: '「w2」は予約語のため使用できません' }),
  isProduction: z.boolean(),
  isActive: z.boolean(),
  behaviorAnalysisLimit: z.number().int().min(100).max(10000),
  recSamplingRate: z.number().int().min(0).max(100),
  // データ連動は W2Commerce 完全連動で固定。画面では非活性で表示し、送信値には含めない
  recMasterMode: z.literal('w2_full'),
  w2Version: z.enum(['v5', 'v6']),
  // 特集ページ画像の取得バッチ。V5区分かつMedia-DRIVER有効のときだけ画面に出す
  featurePageImageBatchEnabled: z.boolean(),
  featurePageImageBatchHour: z.number().min(0).max(23),
  enableRecoMedia: z.boolean(),
  enableContentAnalysis: z.boolean(),
  enableGeoLlmo: z.boolean(),
  enableReview: z.boolean(),
  enableAiPurchaseAssist: z.boolean(),
  enableSearchSuggest: z.boolean(),
  enableTagPages: z.boolean(),
  enablePersona: z.boolean(),
  // 2026-10-06 追加: メールレコメンド（Reco-Media のオプション）
  enableMailReco: z.boolean(),
  enableArticleUpload: z.boolean(),
  enableCarte: z.boolean(),
  enableFullBehavior: z.boolean()
})

export type SiteFormSchemaType = z.infer<typeof siteSchema>

export const convertSiteSchemaToVars = (
  schema: SiteFormSchemaType
): UpdateSiteVars | CreateSiteVars => {
  const base = {
    name: schema.name,
    domain: schema.domain,
    project_number: schema.projectNumber,
    is_test: !schema.isProduction,
    behavior_analysis_limit: schema.behaviorAnalysisLimit,
    rec_sampling_rate: schema.recSamplingRate,
    w2_version: schema.w2Version,
    feature_page_image_batch_enabled: schema.featurePageImageBatchEnabled,
    feature_page_image_batch_hour: schema.featurePageImageBatchHour,
    enable_reco_media: schema.enableRecoMedia,
    enable_content_analysis: schema.enableContentAnalysis,
    enable_geo_llmo: schema.enableGeoLlmo,
    enable_review: schema.enableReview,
    enable_ai_purchase_assist: schema.enableAiPurchaseAssist,
    enable_search_suggest: schema.enableSearchSuggest,
    enable_tag_pages: schema.enableTagPages,
    enable_persona: schema.enablePersona,
    enable_mail_reco: schema.enableMailReco,
    enable_article_upload: schema.enableArticleUpload,
    enable_carte: schema.enableCarte,
    enable_full_behavior: schema.enableFullBehavior
  }
  if (schema.id !== undefined) {
    return { ...base, site_id: schema.id, is_active: schema.isActive }
  }
  return base
}

export const convertSiteToSchema = (site: Site): SiteFormSchemaType => {
  return {
    id: site.id,
    name: site.name,
    domain: site.domain,
    projectNumber: site.project_number || '',
    isProduction: !site.is_test,
    isActive: site.is_active ?? true,
    behaviorAnalysisLimit: site.behavior_analysis_limit ?? 1000,
    recSamplingRate: site.rec_sampling_rate ?? 100,
    recMasterMode: 'w2_full',
    w2Version: (site.w2_version || 'v5') as 'v5' | 'v6',
    featurePageImageBatchEnabled: site.feature_page_image_batch_enabled ?? false,
    featurePageImageBatchHour: site.feature_page_image_batch_hour ?? 3,
    enableRecoMedia: site.enable_reco_media ?? false,
    enableContentAnalysis: site.enable_content_analysis ?? false,
    enableGeoLlmo: site.enable_geo_llmo ?? false,
    enableReview: site.enable_review ?? false,
    enableAiPurchaseAssist: site.enable_ai_purchase_assist ?? false,
    enableSearchSuggest: site.enable_search_suggest ?? false,
    enableTagPages: site.enable_tag_pages ?? false,
    enablePersona: site.enable_persona ?? false,
    enableMailReco: site.enable_mail_reco ?? false,
    enableArticleUpload: site.enable_article_upload ?? false,
    enableCarte: site.enable_carte ?? false,
    enableFullBehavior: site.enable_full_behavior ?? false
  }
}

export const DEFAULT_SITE_SCHEMA: SiteFormSchemaType = {
  name: '',
  domain: '',
  projectNumber: '',
  isProduction: false,
  isActive: true,
  behaviorAnalysisLimit: 1000,
  recSamplingRate: 100,
  recMasterMode: 'w2_full',
  w2Version: 'v5',
  featurePageImageBatchEnabled: false,
  featurePageImageBatchHour: 3,
  enableRecoMedia: false,
  enableContentAnalysis: false,
  enableGeoLlmo: false,
  enableReview: false,
  enableAiPurchaseAssist: false,
  enableSearchSuggest: false,
  enableTagPages: false,
  enablePersona: false,
  enableMailReco: false,
  enableArticleUpload: false,
  enableCarte: false,
  enableFullBehavior: false
}

export const siteNameLabelMapping: NameLabelMapping = {
  name: 'サイト名',
  domain: 'サイトURL',
  projectNumber: 'プロジェクト番号',
  behaviorAnalysisLimit: '行動分析 最大取得件数',
  recSamplingRate: 'レコメンド配信率'
}
